import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { Badge, Box, IconButton, Tooltip } from '@mui/material';
import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line import/order -- path group order differs by config
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AgeFilterValuesE,
  AgentRoleFilterValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
  TicketsRefinementKeysE,
} from 'declarations/enums/tickets';
import type { DmsScheduleAccountsI } from 'declarations/interfaces/dmsScheduleAccounts';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import { formatTooltipWithShortcut } from 'helpers/formatTooltipWithShortcut';
import { useFetchDmsScheduleAccounts } from 'hooks/api/useDmsScheduleAccounts';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTicketRequestSubcategoriesByCategoryIds } from 'hooks/api/useTicketSubcategories';
import useScheduleCards from 'pages/TicketsPage/components/ScheduleCards/hooks/useScheduleCards';

import { OPEN_FILTERS_EVENT } from '../../hooks/useTicketsPageShortcuts';
import useTicketsRefinement from '../TicketsRefinement/hooks/useTicketsRefinement';

import { TicketsFilterChips } from './TicketsFilterChips';
import { TicketsFiltersDropdown } from './TicketsFiltersDropdown';
import type { TicketFilterChipT } from './ticketsFiltersModel';

export type TicketsFiltersBarMobileLayoutProps = {
  filterButtons: React.ReactNode;
  filterChips: React.ReactNode;
};

export type TicketsFiltersBarProps = {
  /** When provided and isMobile, use this to render filter buttons and chips in custom layout */
  renderMobileLayout?: (props: TicketsFiltersBarMobileLayoutProps) => React.ReactNode;
};

export const TicketsFiltersBar: FC<TicketsFiltersBarProps> = ({
  renderMobileLayout,
}): JSX.Element => {
  const { isMobile } = useResponsive();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isDropdownOpen = Boolean(anchorEl);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect((): (() => void) => {
    const handler = (): void => {
      filterButtonRef.current?.click();
    };
    window.addEventListener(OPEN_FILTERS_EVENT, handler);
    return (): void => window.removeEventListener(OPEN_FILTERS_EVENT, handler);
  }, []);

  const {
    activeTab,
    defaultRooftopId,
    getSearchParam,
    oemFamilyId,
    roofTopsList = null,
    selectedRoofTop,
    updateSearchParamsArray,
    isScheduleTicketList,
    selectedSchedule,
    setIsScheduleTicketList,
    setSelectedSchedule,
    clearSearchParam,
  } = useTickets();

  const {
    isAssignedToMeFilterActive,
    isCloseStatusFilterActive,
    isOpenStatusFilterActive,
    handleClearAllFiltersClick,
    handleUpdateFilterClick,
    filtersReset,
  } = useTicketsRefinement();

  const categoriesResult = useFetchTicketRequestCategories(
    true,
    selectedRoofTop || defaultRooftopId
  );

  const scheduleCategoryId = useMemo((): number | null => {
    const scheduleCategory = categoriesResult.data?.find(
      (item: TicketRequestCategoryI): boolean => item.categoryName.toLowerCase() === 'schedule'
    );
    return scheduleCategory ? scheduleCategory.id : null;
  }, [categoriesResult.data]);

  const scheduleCategoryIdsForSubs = useMemo(
    (): number[] =>
      activeTab === ActiveTabTypeE.Schedule && scheduleCategoryId != null
        ? [scheduleCategoryId]
        : [],
    [activeTab, scheduleCategoryId]
  );

  const subCategoriesResult = useFetchTicketRequestSubcategoriesByCategoryIds({
    isEnabled: activeTab === ActiveTabTypeE.Schedule && scheduleCategoryId != null,
    categoryIds: scheduleCategoryIdsForSubs,
    oemFamilyId,
  });

  const selectedCategoryIdsForSubs = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Category) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const ticketSubCategoriesByCategoryResult = useFetchTicketRequestSubcategoriesByCategoryIds({
    isEnabled: activeTab === ActiveTabTypeE.Tickets && selectedCategoryIdsForSubs.length > 0,
    categoryIds: selectedCategoryIdsForSubs,
    oemFamilyId,
  });
  const scheduleAccountsResult = useFetchDmsScheduleAccounts({
    isEnabled: true,
    roofTopId: selectedRoofTop,
  });
  const { scheduleReport } = useScheduleCards();
  const adjustmentSubCategoryIds = useMemo((): string[] => {
    const fromTotal =
      scheduleReport?.totalAdjustedDataBySubCategories?.map((s): string =>
        String(s.subCategoryId)
      ) ?? [];
    if (fromTotal.length > 0) return fromTotal;
    const firstWithData = scheduleReport?.data?.find(
      d => d.adjustedDataBySubCategories && d.adjustedDataBySubCategories.length > 0
    );
    return (
      firstWithData?.adjustedDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? []
    );
  }, [scheduleReport]);

  const idealSubCategoryIds = useMemo((): string[] => {
    const allIds =
      scheduleReport?.totalDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? [];
    const adjustmentSet = new Set(adjustmentSubCategoryIds);
    return allIds.filter((id): boolean => !adjustmentSet.has(id));
  }, [scheduleReport, adjustmentSubCategoryIds]);

  const isAdjustmentsView = useMemo((): boolean => {
    if (adjustmentSubCategoryIds.length === 0) return false;
    const subParam = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';
    const subIds = subParam ? subParam.split(',').map((s): string => s.trim()) : [];
    return (
      subIds.length === adjustmentSubCategoryIds.length &&
      adjustmentSubCategoryIds.every((id): boolean => subIds.includes(id))
    );
  }, [adjustmentSubCategoryIds, getSearchParam]);

  const isIdealView = useMemo((): boolean => {
    if (idealSubCategoryIds.length === 0) return false;
    const subParam = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';
    const subIds = subParam ? subParam.split(',').map((s): string => s.trim()) : [];
    return (
      subIds.length === idealSubCategoryIds.length &&
      idealSubCategoryIds.every((id): boolean => subIds.includes(id))
    );
  }, [idealSubCategoryIds, getSearchParam]);

  const openDropdown = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closeDropdown = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const handleClear = useCallback((): void => {
    handleClearAllFiltersClick();
    closeDropdown();
  }, [handleClearAllFiltersClick, closeDropdown]);

  const selectedCategoryIds = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Category) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const selectedSubCategoryIds = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const selectedRequestSubCategoryIds = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.RequestSubCategoryIds) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const selectedScheduleAccountIds = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.DMSScheduleAccounts) || '';
    return raw.split(',').filter(Boolean);
  }, [getSearchParam]);

  const selectedWorkTypeId = useMemo((): string | null => {
    const raw = getSearchParam(TicketsRefinementKeysE.WorkTypes) || '';
    const first = raw
      .split(',')
      .map((val): string => val.trim())
      .filter(Boolean)[0];
    return first ?? null;
  }, [getSearchParam]);

  const selectedAssigneeNames = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Assignee) || '';
    return raw.split(',').filter(Boolean);
  }, [getSearchParam]);

  const selectedScheduleAgeValues = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Schedule) || '';
    return raw
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }, [getSearchParam]);

  // Single dealer selection (mutually exclusive)
  const selectedDealerId = useMemo((): string | null => {
    const raw = getSearchParam(TicketsRefinementKeysE.RoofTopId) || '';
    return raw || null;
  }, [getSearchParam]);

  const scheduleAgeValueToLabel: Record<string, string> = useMemo(
    () => ({
      [ScheduleFilterValuesE.LessThan31Days]: '0-30 days',
      [ScheduleFilterValuesE.From31To60Days]: '31-60 days',
      [ScheduleFilterValuesE.From61To90Days]: '61-90 days',
      [ScheduleFilterValuesE.From91To120Days]: '91-120 days',
      [ScheduleFilterValuesE.Over120Days]: '121+ days',
    }),
    []
  );

  const chips = useMemo((): TicketFilterChipT[] => {
    const result: TicketFilterChipT[] = [];

    // Pinned defaults (WarrQueue-like)
    result.push({
      key: 'default-my-work',
      label: 'My Work',
      toggled: isAssignedToMeFilterActive,
      deletable: false,
      onClick: handleUpdateFilterClick(TicketsRefinementKeysE.AssignedToMe, 'true'),
    });
    result.push({
      key: 'default-open',
      label: 'Open',
      toggled: isOpenStatusFilterActive,
      deletable: false,
      onClick: handleUpdateFilterClick(TicketsRefinementKeysE.Status, StatusFilterValuesE.Open),
    });
    result.push({
      key: 'default-closed',
      label: 'Closed',
      toggled: isCloseStatusFilterActive,
      deletable: false,
      onClick: handleUpdateFilterClick(TicketsRefinementKeysE.Status, StatusFilterValuesE.Closed),
    });

    // Ticket Age
    const ticketAge = getSearchParam(TicketsRefinementKeysE.TicketAge);
    if (ticketAge === AgeFilterValuesE.Under24Hours) {
      result.push({
        key: 'age-under24',
        label: 'Under 24h',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Under24Hours
        ),
        onDelete: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Under24Hours
        ),
      });
    }
    if (ticketAge === AgeFilterValuesE.Over24Hours) {
      result.push({
        key: 'age-over24',
        label: 'Over 24h',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Over24Hours
        ),
        onDelete: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Over24Hours
        ),
      });
    }

    // Past 30 days
    if (getSearchParam(TicketsRefinementKeysE.Past30Days) === 'true') {
      result.push({
        key: 'past-30-days',
        label: 'Past 30 Days',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(TicketsRefinementKeysE.Past30Days, 'true'),
        onDelete: handleUpdateFilterClick(TicketsRefinementKeysE.Past30Days, 'true'),
      });
    }

    // Past month
    if (getSearchParam(TicketsRefinementKeysE.PastMonth) === 'true') {
      result.push({
        key: 'past-month',
        label: 'Past month',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(TicketsRefinementKeysE.PastMonth, 'true'),
        onDelete: handleUpdateFilterClick(TicketsRefinementKeysE.PastMonth, 'true'),
      });
    }

    // Archived
    if (getSearchParam(TicketsRefinementKeysE.IsArchived) === 'true') {
      result.push({
        key: 'archived',
        label: 'Archived',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(TicketsRefinementKeysE.IsArchived, 'true'),
        onDelete: handleUpdateFilterClick(TicketsRefinementKeysE.IsArchived, 'true'),
      });
    }

    // Role
    const role = getSearchParam(TicketsRefinementKeysE.AgentRoofTopRole);
    if (role === AgentRoleFilterValuesE.Primary) {
      result.push({
        key: 'role-primary',
        label: 'My Primary',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(
          TicketsRefinementKeysE.AgentRoofTopRole,
          AgentRoleFilterValuesE.Primary
        ),
        onDelete: handleUpdateFilterClick(
          TicketsRefinementKeysE.AgentRoofTopRole,
          AgentRoleFilterValuesE.Primary
        ),
      });
    }
    if (role === AgentRoleFilterValuesE.BackUp) {
      result.push({
        key: 'role-backup',
        label: 'My BackUp',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(
          TicketsRefinementKeysE.AgentRoofTopRole,
          AgentRoleFilterValuesE.BackUp
        ),
        onDelete: handleUpdateFilterClick(
          TicketsRefinementKeysE.AgentRoofTopRole,
          AgentRoleFilterValuesE.BackUp
        ),
      });
    }

    // Assign To (AssigneeName)
    selectedAssigneeNames
      .filter((name): boolean => name !== 'All')
      .forEach((name): void => {
        result.push({
          key: `assignee-${name}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedAssigneeNames.filter((n): boolean => n !== name);
            updateSearchParamsArray(TicketsRefinementKeysE.Assignee, next);
          },
          onDelete: (): void => {
            const next = selectedAssigneeNames.filter((n): boolean => n !== name);
            updateSearchParamsArray(TicketsRefinementKeysE.Assignee, next);
          },
        });
      });

    // Request subcategories (Tickets tab only)
    if (activeTab !== ActiveTabTypeE.Schedule) {
      selectedRequestSubCategoryIds.forEach((id): void => {
        const name =
          ticketSubCategoriesByCategoryResult.data?.find((s): boolean => s.id === id)?.name ||
          `Subcategory ${id}`;
        result.push({
          key: `request-subcat-${id}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedRequestSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(
              TicketsRefinementKeysE.RequestSubCategoryIds,
              next.length ? next : null
            );
          },
          onDelete: (): void => {
            const next = selectedRequestSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(
              TicketsRefinementKeysE.RequestSubCategoryIds,
              next.length ? next : null
            );
          },
        });
      });
    }

    // Categories (hidden on Schedule tab)
    activeTab !== ActiveTabTypeE.Schedule &&
      selectedCategoryIds
        .filter((id): boolean => (scheduleCategoryId ? id !== scheduleCategoryId : true))
        .forEach((id): void => {
          const name =
            categoriesResult.data?.find((c): boolean => c.id === id)?.categoryName ||
            `Category ${id}`;
          result.push({
            key: `category-${id}`,
            label: name,
            toggled: true,
            deletable: true,
            onClick: (): void => {
              const next = selectedCategoryIds.filter((v): boolean => v !== id);
              updateSearchParamsArray(TicketsRefinementKeysE.Category, next);
            },
            onDelete: (): void => {
              const next = selectedCategoryIds.filter((v): boolean => v !== id);
              updateSearchParamsArray(TicketsRefinementKeysE.Category, next);
            },
          });
        });

    const DEALER_DEPENDENT_FILTER_KEYS: TicketsRefinementKeysE[] = [
      TicketsRefinementKeysE.RoofTopId,
      TicketsRefinementKeysE.Category,
      TicketsRefinementKeysE.SubCategory,
      TicketsRefinementKeysE.RequestSubCategoryIds,
      TicketsRefinementKeysE.DMSScheduleAccounts,
    ];

    // Dealer (single selection) - show "All" or specific dealer
    if (selectedDealerId) {
      const dealerName =
        roofTopsList?.find(r => r.id === selectedDealerId)?.name || `Dealer ${selectedDealerId}`;
      const handleClearDealer = (): void => {
        clearSearchParam(DEALER_DEPENDENT_FILTER_KEYS);
      };
      result.push({
        key: `dealer-${selectedDealerId}`,
        label: dealerName,
        toggled: true,
        deletable: true,
        onClick: handleClearDealer,
        onDelete: handleClearDealer,
      });
    } else {
      // Show "All" chip when no specific dealer is selected
      result.push({
        key: 'dealer-all',
        label: 'All Dealers',
        toggled: true,
        deletable: false,
        onClick: (): void => {
          // Already on "All", no action needed
        },
      });
    }

    // Schedule file filter (from "View Tickets" in Attached Schedule Files modal)
    if (
      activeTab === ActiveTabTypeE.Schedule &&
      isScheduleTicketList &&
      selectedSchedule &&
      getSearchParam(TicketsRefinementKeysE.ScheduleFileId)
    ) {
      const fileName = selectedSchedule.fileName || `Schedule file`;
      const handleRemoveScheduleFileFilter = (): void => {
        clearSearchParam(TicketsRefinementKeysE.ScheduleFileId);
        setIsScheduleTicketList(false);
        setSelectedSchedule(null);
      };
      result.push({
        key: `schedule-file-${selectedSchedule.id}`,
        label: fileName,
        toggled: true,
        deletable: true,
        onClick: handleRemoveScheduleFileFilter,
        onDelete: handleRemoveScheduleFileFilter,
      });
    }

    if (activeTab === ActiveTabTypeE.Schedule) {
      selectedScheduleAgeValues.forEach((value): void => {
        const baseLabel = scheduleAgeValueToLabel[value] ?? value;
        let label = baseLabel;
        if (isIdealView) label = `Ideal: ${baseLabel}`;
        else if (isAdjustmentsView) label = `Adjustments: ${baseLabel}`;
        let viewKey = 'sched';
        if (isIdealView) viewKey = 'ideal';
        else if (isAdjustmentsView) viewKey = 'adj';
        const shouldClearSubOnEmpty = isAdjustmentsView || isIdealView;
        result.push({
          key: `schedule-age-${value}-${viewKey}`,
          label,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedScheduleAgeValues.filter((v): boolean => v !== value);
            if (next.length === 0 && shouldClearSubOnEmpty) {
              updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
            }
            updateSearchParamsArray(TicketsRefinementKeysE.Schedule, next.length > 0 ? next : null);
          },
          onDelete: (): void => {
            const next = selectedScheduleAgeValues.filter((v): boolean => v !== value);
            if (next.length === 0 && shouldClearSubOnEmpty) {
              updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
            }
            updateSearchParamsArray(TicketsRefinementKeysE.Schedule, next.length > 0 ? next : null);
          },
        });
      });

      selectedSubCategoryIds.forEach((id): void => {
        const name =
          subCategoriesResult.data?.find((s): boolean => s.id === id)?.name || `Subcategory ${id}`;
        result.push({
          key: `subcat-${id}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, next.length ? next : null);
          },
          onDelete: (): void => {
            const next = selectedSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, next.length ? next : null);
          },
        });
      });

      selectedScheduleAccountIds.forEach((id): void => {
        const name =
          scheduleAccountsResult.data?.find((s: DmsScheduleAccountsI): boolean => s.value === id)
            ?.name || `Account ${id}`;
        result.push({
          key: `schedule-acc-${id}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedScheduleAccountIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(
              TicketsRefinementKeysE.DMSScheduleAccounts,
              next.length ? next : null
            );
          },
          onDelete: (): void => {
            const next = selectedScheduleAccountIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(
              TicketsRefinementKeysE.DMSScheduleAccounts,
              next.length ? next : null
            );
          },
        });
      });

      if (selectedWorkTypeId) {
        const name = selectedWorkTypeId;
        const handleClearWorkType = (): void => {
          updateSearchParamsArray(TicketsRefinementKeysE.WorkTypes, null);
        };
        result.push({
          key: `work-type-${selectedWorkTypeId}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: handleClearWorkType,
          onDelete: handleClearWorkType,
        });
      }
    }

    return result;
  }, [
    activeTab,
    categoriesResult.data,
    clearSearchParam,
    getSearchParam,
    isAdjustmentsView,
    isIdealView,
    handleUpdateFilterClick,
    isAssignedToMeFilterActive,
    isCloseStatusFilterActive,
    isOpenStatusFilterActive,
    isScheduleTicketList,
    roofTopsList,
    scheduleAccountsResult.data,
    scheduleAgeValueToLabel,
    scheduleCategoryId,
    selectedAssigneeNames,
    selectedCategoryIds,
    selectedDealerId,
    selectedSchedule,
    selectedScheduleAccountIds,
    selectedWorkTypeId,
    selectedScheduleAgeValues,
    selectedRequestSubCategoryIds,
    selectedSubCategoryIds,
    setSelectedSchedule,
    setIsScheduleTicketList,
    subCategoriesResult.data,
    ticketSubCategoriesByCategoryResult.data,
    updateSearchParamsArray,
  ]);

  const activeNonDefaultFilterCount = useMemo((): number => {
    return chips.filter((c): boolean => c.deletable && c.toggled).length;
  }, [chips]);

  const filterButtons = (
    <>
      <Tooltip title={formatTooltipWithShortcut('Filters', 'openFilters')} arrow>
        <IconButton ref={filterButtonRef} aria-label="Filters" onClick={openDropdown} size="small">
          <Badge badgeContent={activeNonDefaultFilterCount} color="primary">
            <FilterListIcon sx={{ color: (theme): string => theme.palette.primary.main }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title={formatTooltipWithShortcut('Clear filters', 'clearFilters')} arrow>
        <IconButton aria-label="Clear filters" onClick={handleClear} size="small">
          <FilterListOffIcon sx={{ color: (theme): string => theme.palette.primary.main }} />
        </IconButton>
      </Tooltip>
    </>
  );

  const filterChips = (
    <Box
      sx={{
        ...(isMobile ? { width: '100%' } : { ml: 1, flex: 1 }),
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <TicketsFilterChips chips={chips} />
    </Box>
  );

  if (isMobile && renderMobileLayout) {
    return (
      <>
        {renderMobileLayout({ filterButtons, filterChips })}
        <TicketsFiltersDropdown
          anchorEl={anchorEl}
          isOpen={isDropdownOpen}
          onClose={closeDropdown}
          onToggleFilter={handleUpdateFilterClick}
          shouldFiltersReset={filtersReset}
        />
      </>
    );
  }

  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {filterButtons}
        </Box>
        {filterChips}
        <TicketsFiltersDropdown
          anchorEl={anchorEl}
          isOpen={isDropdownOpen}
          onClose={closeDropdown}
          onToggleFilter={handleUpdateFilterClick}
          shouldFiltersReset={filtersReset}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {filterButtons}
      {filterChips}

      <TicketsFiltersDropdown
        anchorEl={anchorEl}
        isOpen={isDropdownOpen}
        onClose={closeDropdown}
        onToggleFilter={handleUpdateFilterClick}
        shouldFiltersReset={filtersReset}
      />
    </Box>
  );
};
